/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './Area';

const AreaManager: ComponentManagerFn<'Area'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Area'>(platform, 'Area'),
    type: 'Area',
    name: 'Area',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ExpressionWatchListener',
          'TriggerOnClick',
          'TriggerOnDoubleClick',
          'TriggerOnMouseEnter',
          'TriggerOnMouseLeave',
          'TriggerOnCreate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        category: 'Data',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer'],
        preventMultiEdit: true,
        required: true,
      } as ViewAttributeChooserProp,
      nameAttribute: {
        label: 'Name Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        required: true,
      } as ViewAttributeChooserProp,
      pathAttribute: {
        label: 'Path Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Textarea', 'JSON'],
        preventMultiEdit: true,
        required: true,
      } as ViewAttributeChooserProp,
      occupancyAttribute: {
        label: 'Occupancy Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => [
          'Percent',
          'Double',
          'Integer',
          'Decimal',
        ],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      fillColor: {
        label: 'Fill Color',
        type: 'ColorExpression',
      },
      opacity: {
        label: 'Opacity',
        type: 'number',
        min: 0.1,
        max: 1,
        step: 0.1,
      },
      activeOpacity: {
        label: 'Hover Opacity',
        type: 'number',
        min: 0.1,
        max: 1,
        step: 0.1,
      },
      hoverDelay: {
        label: 'Hover Delay',
        type: 'number',
        min: 50,
        max: 1000,
        step: 50,
      },
    },
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'pathAttribute':
        case 'idAttribute':
        case 'nameAttribute':
          return !!itemData.alias;
        default:
          return true;
      }
    },
    replaceableWith: ['Device', 'Tracker', 'Pulse'],
    getDisplayText: (item) => item.props.nameAttribute,
    isList: true,
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The Area component is a specialized sub-component used strictly within an **IoT Image Mapper** or similar canvas-based visualization. It represents a defined zone or polygon overlaid on top of a map, floor plan, or schematic image.
Use this component to visualize spatial data—such as defining rooms on a floor plan, territories on a map, or zones in a warehouse—and to display status metrics (like occupancy or temperature) for that specific zone.

**1. Data Connection**<br/>
The Area component connects to a Data Store that contains the geometric definitions of the zones.
* **Alias:** The Data Store containing the area records.
* **Path Attribute:** The field containing the SVG path data (e.g., M10 10 H 90 V 90 H 10 Z) or JSON coordinates that define the shape of the polygon.
* **ID Attribute:** The unique identifier for the zone (e.g., "Room-101").
* **Name Attribute:** The display name of the zone (e.g., "Conference Room A").
* **Occupancy Attribute:** A numeric field (Percent, Integer) used to visualize density or usage status.

**2. Visual Styling**<br/>
You can control how the polygon overlay appears to the user to convey status at a glance.
* **Fill Color:** Sets the background color of the polygon.
* **Dynamic Coloring:** This is an expression field, allowing you to change the color based on data (e.g., return "Red" if occupancy > 90, else "Green").
* **Opacity:** Sets the transparency of the polygon (0.1 to 1.0).
* **Hover Opacity:** Sets the transparency level when the user hovers over the zone (active state), providing immediate visual feedback.

**3. Interactivity**<br/>
The Area component transforms a static image into an interactive spatial dashboard.
* **Triggers:** Supports interaction events like **TriggerOnClick**, **TriggerOnDoubleClick**, **TriggerOnMouseEnter**, and **TriggerOnMouseLeave**.
* **Use Case:** Hovering over a "Conference Room" zone could trigger a popup showing the meeting schedule; clicking it could open the room's detailed sensor log.
* **Hover Delay:** Controls the responsiveness of the hover effect (in milliseconds) to prevent accidental triggers while moving the mouse across the map.

**4. Best Use Cases**<br/>
* **Smart Buildings:** Visualizing room occupancy on an office floor plan.
* **Warehouse Management:** Defining storage zones (Aisle 1, Aisle 2) on a layout map to show inventory levels or hot zones.
* **Parking Lots:** Drawing polygons for individual parking spots and coloring them Red/Green based on real-time sensor data.
* **Industrial Monitoring:** Defining "Safety Zones" around heavy machinery on a factory schematic to monitor personnel proximity.`,
  };
};

export default AreaManager;
